import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Eye } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { tripService } from '../../services/trip.service';
import { TripStatus, UserRole } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { useAuthStore } from '../../store/auth.store';

export const TripsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasAnyRole, user } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  
  const canCreateTrip = hasAnyRole([UserRole.ADMIN, UserRole.FLEET_MANAGER]);
  const isDriver = hasAnyRole([UserRole.DRIVER]);
  const driverId = user?.driverId;

  const { data: trips, isLoading } = useQuery({
    queryKey: isDriver && driverId ? ['trips', 'driver', driverId] : ['trips'],
    queryFn: () => isDriver && driverId ? tripService.getByDriver(driverId) : tripService.getAll(),
    enabled: !isDriver || !!driverId,
  });

  const getStatusBadgeVariant = (status: TripStatus) => {
    switch (status) {
      case TripStatus.PLANNED:
        return 'info';
      case TripStatus.ACTIVE:
        return 'success';
      case TripStatus.COMPLETED:
        return 'default';
      case TripStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredTrips = trips?.filter((trip) => {
    const matchesSearch = trip.tripNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trip.vehicleRegistration.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trip.driverName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || trip.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-charcoal mb-2">
            {isDriver ? 'My Trips' : 'Trip Management'}
          </h1>
          <p className="text-gray-600">
            {isDriver ? 'View and manage your assigned trips' : 'Manage and track all trips'}
          </p>
        </div>
        {canCreateTrip && (
          <Button onClick={() => navigate('/trips/create')} variant="primary">
            <Plus size={20} className="mr-2" />
            Create Trip
          </Button>
        )}
      </div>

      <Card>
        <div className="mb-4 flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder="Search by trip number, vehicle, or driver..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Statuses</option>
            {Object.values(TripStatus).map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading trips...</p>
          </div>
        ) : filteredTrips && filteredTrips.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trip Number</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>Distance</TableHead>
                <TableHead>Weight</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTrips.map((trip) => (
                <TableRow key={trip.id}>
                  <TableCell className="font-medium">{trip.tripNumber}</TableCell>
                  <TableCell>{trip.vehicleRegistration}</TableCell>
                  <TableCell>{trip.driverName}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>{trip.origin}</div>
                      <div className="text-gray-500">→ {trip.destination}</div>
                    </div>
                  </TableCell>
                  <TableCell>{format(parseISO(trip.startDate), 'MMM dd, yyyy')}</TableCell>
                  <TableCell>
                    {trip.actualDistanceKm ? `${trip.actualDistanceKm} km` : `${trip.plannedDistanceKm} km (planned)`}
                  </TableCell>
                  <TableCell>
                    {trip.weightInTons ? `${trip.weightInTons} tons` : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(trip.status)}>
                      {trip.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/trips/${trip.id}`)}
                        className="p-1 text-primary hover:bg-blue-50 rounded transition-colors"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No trips found</p>
          </div>
        )}
      </Card>
    </div>
  );
};
